// src/App.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Controls from './components/Controls';
import Grid from './components/Grid';
import Grid1D from './components/Grid1D';
import Menu from './components/Menu';
import ModeMenu from './components/ModeMenu';
import PatternSearchModal from './components/PatternSearchModal';
import { GRID_SIZE, DEFAULT_DEBUG_CONFIG, CELL_PIXEL_SIZE } from "./config";
import { modes, availableModes, getPatternsForMode } from './modes';
import { useTranslation } from './i18n';
import { decodeRle, isRleString } from './utils/rle';

const gridsEqual = (gridA, gridB) => {
    if (gridA === gridB) {
        return true;
    }
    if (!gridA || !gridB || gridA.length !== gridB.length) {
        return false;
    }
    for (let row = 0; row < gridA.length; row++) {
        const rowA = gridA[row];
        const rowB = gridB[row];
        if (!rowA || !rowB || rowA.length !== rowB.length) {
            return false;
        }
        for (let col = 0; col < rowA.length; col++) {
            if (rowA[col] !== rowB[col]) {
                return false;
            }
        }
    }
    return true;
};

const getBoundingBox = (grid) => {
    let minR = Infinity, maxR = -Infinity, minC = Infinity, maxC = -Infinity;
    let hasCells = false;
    for (let r = 0; r < grid.length; r++) {
        for (let c = 0; c < grid[r].length; c++) {
            if (grid[r][c] !== 0) {
                if (r < minR) minR = r;
                if (r > maxR) maxR = r;
                if (c < minC) minC = c;
                if (c > maxC) maxC = c;
                hasCells = true;
            }
        }
    }
    return hasCells ? { minR, maxR, minC, maxC } : null;
};

const areGridsShiftedEqual = (gridA, gridB) => {
    const boxA = getBoundingBox(gridA);
    const boxB = getBoundingBox(gridB);

    if (!boxA && !boxB) return true;
    if (!boxA || !boxB) return false;

    const heightA = boxA.maxR - boxA.minR + 1;
    const widthA = boxA.maxC - boxA.minC + 1;
    const heightB = boxB.maxR - boxB.minR + 1;
    const widthB = boxB.maxC - boxB.minC + 1;

    if (heightA !== heightB || widthA !== widthB) return false;

    for (let r = 0; r < heightA; r++) {
        for (let c = 0; c < widthA; c++) {
            if (gridA[boxA.minR + r][boxA.minC + c] !== gridB[boxB.minR + r][boxB.minC + c]) {
                return false;
            }
        }
    }
    return true;
};


const App = () => {
    const [isRunning, setIsRunning] = useState(false);
    const [grid, setGridState] = useState(
        Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0))
    );
    const gridRef = useRef(grid);
    const [speed, setSpeed] = useState(500);
    const [generation, setGeneration] = useState(0);
    const [model, setModel] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const modeParam = params.get('m') || params.get('mode');
        return modes[modeParam] ? modeParam : 'classic';
    });

    const [modeParams, setModeParams] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const modeParam = params.get('m') || params.get('mode') || 'classic';
        const targetMode = modes[modeParam] || modes.classic;
        const defaultParams = targetMode.getDefaultParams();

        if (['1d', 'halfLife', 'testMode'].includes(modeParam)) {
            const merged = { ...defaultParams };

            // Explicitly define long keys for 1D mode
            const longKeys = ['neighborhoodSize', 'birthRules', 'survivalRules', 'symmetric'];
            const keyMap = {
                n: 'neighborhoodSize',
                b: 'birthRules',
                s: 'survivalRules',
                y: 'symmetric'
            };

            // Parse short and long keys
            params.forEach((value, key) => {
                if (key === 'm' || key === 'mode') return;

                const longKey = keyMap[key] || key;
                // If it's a known configurable parameter for these modes
                if (longKeys.includes(longKey) || longKey.startsWith('weight') || merged[longKey] !== undefined) {
                    const defaultValue = merged[longKey];
                    // Some modes (like halfLife) might not have defined a default but we know it's a valid string param rule
                    if (typeof defaultValue === 'number') {
                        merged[longKey] = parseFloat(value);
                    } else if (typeof defaultValue === 'boolean') {
                        merged[longKey] = value === 'true' || value === '1';
                    } else if (longKey.startsWith('weight')) {
                        // For weights not in defaults yet
                        merged[longKey] = parseFloat(value);
                    } else {
                        merged[longKey] = value;
                    }
                } else if (['birthRules', 'survivalRules'].includes(longKey)) {
                    // Force inject rules even if defaultParams didn't pre-define them 
                    // (useful for extensible parsing)
                    merged[longKey] = value;
                }
            });

            if (modeParam === '1d') {
                // Parse combined weight string 'w=1,1,1,1...' (overrides individual weights)
                const weightString = params.get('w');
                if (weightString) {
                    const weights = weightString.split(',').map(parseFloat);
                    weights.forEach((w, i) => {
                        const radius = Math.floor(i / 2) + 1;
                        const isPlus = i % 2 === 1;
                        const key = `weight${isPlus ? 'Plus' : 'Minus'}${radius}`;
                        merged[key] = w;
                    });
                }
            }

            return merged;
        }

        return defaultParams;
    });

    const [patternLibrary, setPatternLibrary] = useState(null);


    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const currentModeInUrl = params.get('m') || params.get('mode');

        // Update mode if it changed (prefer short key 'm')
        if (currentModeInUrl !== model) {
            params.set('m', model);
            params.delete('mode');
        }

        const nextMode = modes[model] || modes.classic;
        const defaults = nextMode.getDefaultParams();

        if (['1d', 'halfLife', 'testMode'].includes(model)) {
            const keyMap = {
                neighborhoodSize: 'n',
                birthRules: 'b',
                survivalRules: 's',
                symmetric: 'y'
            };

            // 1. Sync basic params (n, b, s, y)
            Object.entries(keyMap).forEach(([longKey, shortKey]) => {
                const value = modeParams[longKey];
                const defaultValue = defaults[longKey];

                if (value !== undefined && value !== defaultValue) {
                    params.set(shortKey, value.toString());
                } else {
                    params.delete(shortKey);
                }
                params.delete(longKey); // Always clean up legacy long key
            });

            if (model === '1d') {
                // 2. Sync weights (w) only for 1D
                const neighborhoodSize = Number(modeParams.neighborhoodSize) || 2;
                const weightValues = [];
                for (let i = 1; i <= neighborhoodSize; i++) {
                    weightValues.push(modeParams[`weightMinus${i}`] ?? 1);
                    weightValues.push(modeParams[`weightPlus${i}`] ?? 1);
                }

                const isAllDefault = weightValues.every(v => v === 1);
                if (!isAllDefault) {
                    params.set('w', weightValues.join(','));
                } else {
                    params.delete('w');
                }

                // 3. Clean up individual weight keys if they exist in URL
                const allKeys = Array.from(params.keys());
                allKeys.forEach(key => {
                    if (key.startsWith('weightMin') || key.startsWith('weightPlu')) {
                        params.delete(key);
                    }
                });
            } else {
                // If Half-Life, Exclusive Half-Life, or Test Mode, clean up weight params
                params.delete('w');
                Array.from(params.keys()).forEach(key => {
                    if (key.startsWith('weight')) params.delete(key);
                });
            }
        } else {
            // Other modes: clean up ALL specific params
            const keysToRemove = ['n', 'b', 's', 'y', 'w'];
            keysToRemove.forEach(k => params.delete(k));

            // Also clean up any keys starting with 'weight'
            Array.from(params.keys()).forEach(key => {
                if (key.startsWith('weight')) params.delete(key);
            });
        }

        // Ensure ?p= and ?t= are always last for readable URLs
        const patternParam = params.get('p');
        const stepsParam = params.get('t');
        if (patternParam) {
            params.delete('p');
            params.delete('t');
            params.set('p', patternParam);
            if (stepsParam) params.set('t', stepsParam);
        }

        const newUrlParams = params.toString();
        const currentUrlParams = new URLSearchParams(window.location.search).toString();

        if (currentUrlParams !== newUrlParams) {
            window.history.replaceState(null, '', `?${newUrlParams}`);
        }
    }, [model, modeParams]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
    const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
    const [initialViewCenter, setInitialViewCenter] = useState(null);
    const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
    const [controlAnchor, setControlAnchor] = useState('bottom-right');
    const [controlFreePosition, setControlFreePosition] = useState({ x: 0, y: 0 });
    const [isDraggingControls, setIsDraggingControls] = useState(false);
    const [controlDragCoords, setControlDragCoords] = useState({ x: 0, y: 0 });
    const [cellPixelSize, setCellPixelSize] = useState(CELL_PIXEL_SIZE);
    const [isTestingPeriodicity, setIsTestingPeriodicity] = useState(false);
    const [isPatternSearchOpen, setIsPatternSearchOpen] = useState(false);
    const [detectedPeriod, setDetectedPeriod] = useState(null);
    const [selectedPattern, setSelectedPattern] = useState(null);
    const [pasteGhost, setPasteGhost] = useState(null);
    const clearPasteGhost = useCallback(() => setPasteGhost(null), []);
    const controlsRef = useRef(null);
    const playAreaRef = useRef(null);
    const dragOffsetRef = useRef({ x: 0, y: 0 });
    const testingHistoryRef = useRef([]);
    const testingIntervalRef = useRef(null);
    const testGenerationRef = useRef(0);
    const maxTestingHistorySize = 2000;
    const debugConfig = DEFAULT_DEBUG_CONFIG;
    const { t } = useTranslation();

    const prevModelRef = useRef(model);

    useEffect(() => {
        gridRef.current = grid;
    }, [grid]);

    const cloneGrid = useCallback((sourceGrid) => sourceGrid.map((row) => [...row]), []);
    const historyRef = useRef([]);
    const maxHistorySize = 100;

    const applyGridChange = useCallback((updater, { skipHistory = false } = {}) => {
        setGridState((prev) => {
            const nextGrid = typeof updater === 'function' ? updater(prev) : updater;
            if (gridsEqual(prev, nextGrid)) {
                return prev;
            }
            if (!skipHistory) {
                historyRef.current.push(cloneGrid(prev));
                if (historyRef.current.length > maxHistorySize) {
                    historyRef.current.shift();
                }
            }
            return nextGrid;
        });
    }, [cloneGrid]);

    useEffect(() => {
        // When the selected mode changes from the UI, we reset the modeParams
        // to that new mode's defaults to prevent "parameter bleed" from the old mode.
        if (prevModelRef.current !== model) {
            const nextMode = modes[model] || modes.classic;
            setModeParams(nextMode.getDefaultParams());

            // Clear grid and pattern state on mode change
            applyGridChange(Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0)));
            setGeneration(0);
            setDetectedPeriod(null);
            setInitialViewCenter(null);
            setIsRunning(false);

            // Force clean URL parameters for patterns
            const params = new URLSearchParams(window.location.search);
            if (params.has('p') || params.has('t')) {
                params.delete('p');
                params.delete('t');
                const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
                window.history.replaceState(null, '', newUrl);
            }

            prevModelRef.current = model;
        }
    }, [model, applyGridChange]);

    useEffect(() => {
        import('./patterns/index.js')
            .then(module => setPatternLibrary(module.patternLibrary))
            .catch(err => console.error("Failed to load generic patterns:", err));
    }, []);

    const loadAndCenterPattern = useCallback((pattern, options = {}) => {
        const { clearBefore = true, steps = 0 } = options;
        if (!pattern || !pattern.cells) return;

        const currentMode = modes[model] || modes.classic;
        const parsedCells = currentMode.parseCells(pattern.cells);
        const center = Math.floor(GRID_SIZE / 2);

        // Compute pattern bounding box to center the view on it
        const rows = parsedCells.map(c => c[0]);
        const cols = parsedCells.map(c => c[1]);
        const minR = Math.min(...rows);
        const maxR = Math.max(...rows);
        const minC = Math.min(...cols);
        const maxC = Math.max(...cols);
        const patternCenterRow = center + (minR + maxR) / 2;
        const patternCenterCol = center + (minC + maxC) / 2;

        // Build the grid
        applyGridChange((prevGrid) => {
            let nextGrid = clearBefore
                ? Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0))
                : prevGrid.map(row => [...row]);

            parsedCells.forEach(([row, col, value]) => {
                const r = row + center;
                const c = col + center;
                if (r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE) {
                    nextGrid[r][c] = value;
                }
            });

            // Advance steps if needed
            for (let i = 0; i < steps; i++) {
                nextGrid = nextGrid.map((row, rowIndex) =>
                    row.map((cell, colIndex) =>
                        currentMode.computeNextState(nextGrid, rowIndex, colIndex, modeParams, i)
                    )
                );
            }

            return nextGrid;
        });

        setGeneration(steps);
        setIsRunning(false);
        setInitialViewCenter({ row: patternCenterRow, col: patternCenterCol });
    }, [model, modeParams, applyGridChange]);

    // Auto-load a pattern from the URL ?p= parameter, optionally advance ?t= steps
    useEffect(() => {
        if (!patternLibrary) return;

        const params = new URLSearchParams(window.location.search);
        const patternName = params.get('p');
        if (!patternName) return;

        const patterns = getPatternsForMode(patternLibrary, model, modeParams);
        const pattern = patterns[patternName];
        if (!pattern) {
            console.warn(`Pattern "${patternName}" not found for mode "${model}"`);
            return;
        }

        const steps = parseInt(params.get('t'), 10) || 0;
        loadAndCenterPattern(pattern, { clearBefore: true, steps });
    }, [patternLibrary, loadAndCenterPattern]); // eslint-disable-line

    const undo = useCallback(() => {
        while (historyRef.current.length) {
            const previous = historyRef.current.pop();
            if (previous && !gridsEqual(previous, gridRef.current)) {
                setGridState(previous);
                setIsRunning(false);
                return;
            }
        }
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            const isUndo = (event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'z';
            if (isUndo) {
                event.preventDefault();
                undo();
            }
        };

        const handlePaste = (event) => {
            // Do not intercept paste if the user is typing in an input or textarea
            if (['INPUT', 'TEXTAREA'].includes(event.target.tagName)) return;

            try {
                const pastedText = event.clipboardData.getData('text');
                if (!pastedText) return;

                if (isRleString(pastedText)) {
                    const currentMode = modes[model] || modes.classic;
                    const decoded = decodeRle(pastedText, currentMode.rleStateMap);
                    if (decoded.cells && decoded.cells.length > 0) {
                        setPasteGhost({ cells: decoded.cells, name: decoded.name, description: decoded.description });
                    }
                }
            } catch (err) {
                // Not valid RLE, safely ignore
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('paste', handlePaste);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('paste', handlePaste);
        };
    }, [undo, applyGridChange, model]);

    const changeSpeed = (newSpeed) => setSpeed(newSpeed);
    const toggleRun = () => setIsRunning(prev => !prev);
    const clearGrid = () => {
        applyGridChange(Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0)));
        setIsRunning(false);
        setIsTestingPeriodicity(false);
        setGeneration(0);
        setDetectedPeriod(null);
        setInitialViewCenter(null);
        testingHistoryRef.current = [];
        if (testingIntervalRef.current) {
            clearInterval(testingIntervalRef.current);
            testingIntervalRef.current = null;
        }

        // Remove pattern and generation params from URL
        const params = new URLSearchParams(window.location.search);
        params.delete('p');
        params.delete('t');
        const newUrlParams = params.toString();
        window.history.replaceState(null, '', `?${newUrlParams}`);

        // Reset 1D mode specific state if needed
        if (model === '1d') {
            // The Grid1D component will handle resetting its own state
        }
    };
    const nextGeneration = () => {
        const mode = modes[model] || modes.classic;
        applyGridChange((prevGrid) =>
            prevGrid.map((row, rowIndex) =>
                row.map((cell, colIndex) =>
                    mode.computeNextState(prevGrid, rowIndex, colIndex, modeParams, generation)
                )
            )
        );
        setGeneration(prev => prev + 1);
    };

    const testPeriodicity = useCallback(() => {
        if (isTestingPeriodicity) {
            // Stop testing
            setIsTestingPeriodicity(false);
            if (testingIntervalRef.current) {
                clearInterval(testingIntervalRef.current);
                testingIntervalRef.current = null;
            }
            return;
        }

        // Start testing
        setIsTestingPeriodicity(true);
        setIsRunning(false); // Stop normal simulation if running
        setDetectedPeriod(null);
        testingHistoryRef.current = [];
        testGenerationRef.current = generation;

        const initialGrid = cloneGrid(grid);
        const initialBox = getBoundingBox(initialGrid);
        testingHistoryRef.current.push({ grid: initialGrid, generation: testGenerationRef.current, box: initialBox });

        const testNextGeneration = () => {
            const mode = modes[model] || modes.classic;
            const currentGrid = gridRef.current;

            // Compute next generation
            const nextGrid = currentGrid.map((row, rowIndex) =>
                row.map((cell, colIndex) =>
                    mode.computeNextState(currentGrid, rowIndex, colIndex, modeParams, testGenerationRef.current)
                )
            );

            testGenerationRef.current += 1;
            const nextGridSnapshot = cloneGrid(nextGrid);
            const nextBox = getBoundingBox(nextGridSnapshot);

            // Check for periodicity
            for (let i = testingHistoryRef.current.length - 1; i >= 0; i--) {
                const { grid: savedGrid, generation: savedGen, box: savedBox } = testingHistoryRef.current[i];

                let isEqual = false;
                if (!nextBox && !savedBox) {
                    isEqual = true;
                } else if (nextBox && savedBox) {
                    const heightA = nextBox.maxR - nextBox.minR + 1;
                    const widthA = nextBox.maxC - nextBox.minC + 1;
                    const heightB = savedBox.maxR - savedBox.minR + 1;
                    const widthB = savedBox.maxC - savedBox.minC + 1;

                    if (heightA === heightB && widthA === widthB) {
                        isEqual = true;
                        for (let r = 0; r < heightA; r++) {
                            for (let c = 0; c < widthA; c++) {
                                if (nextGridSnapshot[nextBox.minR + r][nextBox.minC + c] !== savedGrid[savedBox.minR + r][savedBox.minC + c]) {
                                    isEqual = false;
                                    break;
                                }
                            }
                            if (!isEqual) break;
                        }
                    }
                }

                if (isEqual) {
                    const period = testGenerationRef.current - savedGen;
                    setDetectedPeriod(period);
                    setIsTestingPeriodicity(false);

                    // Show the successful shifted/identical grid state
                    setGridState(nextGridSnapshot);
                    setGeneration(testGenerationRef.current);

                    if (testingIntervalRef.current) {
                        clearInterval(testingIntervalRef.current);
                        testingIntervalRef.current = null;
                    }
                    return;
                }
            }

            // Store snapshot
            testingHistoryRef.current.push({ grid: nextGridSnapshot, generation: testGenerationRef.current, box: nextBox });
            if (testingHistoryRef.current.length > maxTestingHistorySize) {
                testingHistoryRef.current.shift();
            }

            // Update grid visually (but don't update generation counter)
            setGridState(nextGrid);

            // Check if we've exceeded a reasonable limit
            if (testGenerationRef.current > maxTestingHistorySize) {
                setIsTestingPeriodicity(false);
                setGridState(nextGridSnapshot);
                setGeneration(testGenerationRef.current);

                if (testingIntervalRef.current) {
                    clearInterval(testingIntervalRef.current);
                    testingIntervalRef.current = null;
                }
                setDetectedPeriod(null);
            }
        };

        testingIntervalRef.current = setInterval(testNextGeneration, 10); // Fast testing
    }, [isTestingPeriodicity, generation, grid, model, modeParams, cloneGrid]);

    useEffect(() => {
        let interval;
        if (isRunning) {
            interval = setInterval(() => nextGeneration(), speed);
        }
        return () => clearInterval(interval);
    }, [isRunning, speed, grid, model, modeParams]);

    useEffect(() => {
        return () => {
            if (testingIntervalRef.current) {
                clearInterval(testingIntervalRef.current);
            }
        };
    }, []);

    const loadPattern = (pattern, rowOffset, colOffset, options = {}) => {
        const { clearBefore = false } = options;
        console.log('Loading pattern at:', { rowOffset, colOffset, clearBefore }, pattern);
        if (!pattern || !pattern.cells) {
            console.error('Invalid pattern data:', pattern);
            return;
        }

        const currentMode = modes[model] || modes.classic;
        const parsedCells = currentMode.parseCells(pattern.cells);

        applyGridChange((prevGrid) => {
            let newGrid;
            if (clearBefore) {
                newGrid = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0));
            } else {
                newGrid = prevGrid.map(row => [...row]);
            }

            parsedCells.forEach(([row, col, value]) => {
                const finalRow = row + rowOffset;
                const finalCol = col + colOffset;

                if (finalRow >= 0 && finalRow < GRID_SIZE && finalCol >= 0 && finalCol < GRID_SIZE) {
                    newGrid[finalRow][finalCol] = value;
                }
            });
            return newGrid;
        });
        setGeneration(0);
        setIsRunning(false);
    };

    const loadConfiguration = (config) => {
        console.log('Loading configuration:', config);
        if (!config || !config.cells) {
            console.error('Invalid configuration data:', config);
            return;
        }

        const currentMode = modes[model] || modes.classic;
        const parsedCells = currentMode.parseCells(config.cells);

        applyGridChange(() => {
            const newGrid = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0));

            parsedCells.forEach(([row, col, value]) => {
                if (row >= 0 && row < GRID_SIZE && col >= 0 && col < GRID_SIZE) {
                    newGrid[row][col] = value;
                }
            });
            return newGrid;
        });
        setGeneration(0);
        setIsRunning(false);
    };

    const handleLoadConfigurationFromFile = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const currentMode = modes[model] || modes.classic;
                const decoded = decodeRle(text, currentMode.rleStateMap);
                if (decoded.cells && decoded.cells.length > 0) {
                    loadConfiguration({ cells: decoded.cells, name: decoded.name, description: decoded.description });
                } else {
                    alert(t('alerts.invalidConfigurationFile'));
                }
            } catch (error) {
                console.error('Error loading configuration:', error);
                alert(t('alerts.invalidConfigurationFile'));
            }
        };
        reader.readAsText(file);
    };

    const handleControlDragStart = (event) => {
        if (!controlsRef.current || !playAreaRef.current) return;
        const controlRect = controlsRef.current.getBoundingClientRect();
        const parentRect = playAreaRef.current.getBoundingClientRect();
        dragOffsetRef.current = {
            x: event.clientX - controlRect.left,
            y: event.clientY - controlRect.top,
        };
        setControlDragCoords({
            x: controlRect.left - parentRect.left,
            y: controlRect.top - parentRect.top,
        });
        setIsDraggingControls(true);
        document.body.style.userSelect = 'none';
        event.preventDefault();
    };

    useEffect(() => {
        if (!isDraggingControls) {
            document.body.style.userSelect = '';
            return;
        }

        const handleMouseMove = (event) => {
            if (!controlsRef.current || !playAreaRef.current) return;
            const parentRect = playAreaRef.current.getBoundingClientRect();
            const controlRect = controlsRef.current.getBoundingClientRect();

            const maxX = Math.max(0, parentRect.width - controlRect.width);
            const maxY = Math.max(0, parentRect.height - controlRect.height);

            let x = event.clientX - parentRect.left - dragOffsetRef.current.x;
            let y = event.clientY - parentRect.top - dragOffsetRef.current.y;

            x = Math.max(0, Math.min(x, maxX));
            y = Math.max(0, Math.min(y, maxY));

            setControlDragCoords({ x, y });
        };

        const handleMouseUp = () => {
            if (controlsRef.current && playAreaRef.current) {
                const parentRect = playAreaRef.current.getBoundingClientRect();
                const controlRect = controlsRef.current.getBoundingClientRect();
                const relativeLeft = controlRect.left - parentRect.left;
                const relativeTop = controlRect.top - parentRect.top;

                const horizontalLimit = Math.max(0, parentRect.width - controlRect.width);
                const verticalLimit = Math.max(0, parentRect.height - controlRect.height);

                const margin = 24;
                const headerOffset = 96;
                const leftX = Math.min(margin, horizontalLimit);
                const rightX = Math.max(horizontalLimit - margin, leftX);
                const topY = Math.min(headerOffset, verticalLimit);
                const bottomY = Math.max(verticalLimit - margin, topY);

                const anchorPoints = {
                    'top-left': { x: leftX, y: topY },
                    'top-right': { x: rightX, y: topY },
                    'bottom-left': { x: leftX, y: bottomY },
                    'bottom-right': { x: rightX, y: bottomY },
                };

                const nearest = Object.entries(anchorPoints).reduce((closest, [key, point]) => {
                    const distance = Math.hypot(relativeLeft - point.x, relativeTop - point.y);
                    if (!closest || distance < closest.distance) {
                        return { key, distance };
                    }
                    return closest;
                }, null);

                const threshold = 80;
                if (nearest && nearest.distance <= threshold) {
                    setControlAnchor(nearest.key);
                    setControlFreePosition(anchorPoints[nearest.key]);
                } else {
                    const clampedX = Math.max(0, Math.min(relativeLeft, horizontalLimit));
                    const clampedY = Math.max(0, Math.min(relativeTop, verticalLimit));
                    setControlAnchor('free');
                    setControlFreePosition({ x: clampedX, y: clampedY });
                    setControlDragCoords({ x: clampedX, y: clampedY });
                }
            }

            setIsDraggingControls(false);
            document.body.style.userSelect = '';
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingControls]);

    const anchorStyles = {
        'top-left': { top: '6rem', left: '1.5rem' },
        'top-right': { top: '6rem', right: '1.5rem' },
        'bottom-left': { bottom: '1.5rem', left: '1.5rem' },
        'bottom-right': { bottom: '1.5rem', right: '1.5rem' },
    };

    const controlContainerStyle = isDraggingControls
        ? { top: `${controlDragCoords.y}px`, left: `${controlDragCoords.x}px`, right: 'auto', bottom: 'auto' }
        : controlAnchor !== 'free'
            ? anchorStyles[controlAnchor]
            : { top: `${controlFreePosition.y}px`, left: `${controlFreePosition.x}px` };

    const patternsForMode = getPatternsForMode(patternLibrary, model, modeParams);

    return (
        <div className="flex h-screen">
            <Menu
                isOpen={isMenuOpen}
                setIsOpen={setIsMenuOpen}
                mode={model}
                modeParams={modeParams}
                patterns={patternsForMode}
                grid={grid}
                loadPattern={loadPattern}
                loadAndCenterPattern={loadAndCenterPattern}
                loadConfiguration={loadConfiguration}
                loadConfigurationFromFile={handleLoadConfigurationFromFile}
                cellPixelSize={cellPixelSize}
                selectedPattern={selectedPattern}
                setSelectedPattern={setSelectedPattern}
            />
            <div className="relative flex flex-1 flex-col bg-gray-900" ref={playAreaRef}>
                {model === '1d' ? (
                    <Grid1D
                        grid={grid}
                        setGrid={applyGridChange}
                        isRunning={isRunning}
                        speed={speed}
                        nextGeneration={nextGeneration}
                        onOffsetChange={setCanvasOffset}
                        onDimensionsChange={setCanvasDimensions}
                        loadPattern={loadPattern}
                        model={model}
                        setModel={setModel}
                        availableModes={availableModes}
                        setIsModeMenuOpen={setIsModeMenuOpen}
                        setIsMenuOpen={setIsMenuOpen}
                        onOpenPatternSearch={() => setIsPatternSearchOpen(true)}
                        renderCell={modes[model]?.renderCell || modes.classic.renderCell}
                        cellPixelSize={cellPixelSize}
                        onCellPixelSizeChange={setCellPixelSize}
                        generation={generation}
                        debugConfig={debugConfig}
                        selectedPattern={selectedPattern}
                        setSelectedPattern={setSelectedPattern}
                        pasteGhost={pasteGhost}
                        clearPasteGhost={clearPasteGhost}
                    />
                ) : (
                    <Grid
                        grid={grid}
                        setGrid={applyGridChange}
                        isRunning={isRunning}
                        speed={speed}
                        nextGeneration={nextGeneration}
                        onOffsetChange={setCanvasOffset}
                        onDimensionsChange={setCanvasDimensions}
                        loadPattern={loadPattern}
                        model={model}
                        setModel={setModel}
                        availableModes={availableModes}
                        setIsModeMenuOpen={setIsModeMenuOpen}
                        setIsMenuOpen={setIsMenuOpen}
                        onOpenPatternSearch={() => setIsPatternSearchOpen(true)}
                        renderCell={modes[model]?.renderCell || modes.classic.renderCell}
                        cellPixelSize={cellPixelSize}
                        onCellPixelSizeChange={setCellPixelSize}
                        generation={generation}
                        debugConfig={debugConfig}
                        pasteGhost={pasteGhost}
                        clearPasteGhost={clearPasteGhost}
                        initialViewCenter={initialViewCenter}
                    />
                )}
                <div
                    className="pointer-events-none absolute z-30"
                    style={controlContainerStyle}
                >
                    <div ref={controlsRef} className="pointer-events-auto">
                        <Controls
                            isRunning={isRunning}
                            runOrStop={toggleRun}
                            onReset={clearGrid}
                            changeSpeed={changeSpeed}
                            speed={speed}
                            nextGeneration={nextGeneration}
                            generation={generation}
                            onDragHandleMouseDown={handleControlDragStart}
                            isDragging={isDraggingControls}
                            testPeriodicity={testPeriodicity}
                            isTestingPeriodicity={isTestingPeriodicity}
                            detectedPeriod={detectedPeriod}
                        />
                    </div>
                </div>
            </div>
            <ModeMenu
                isOpen={isModeMenuOpen}
                setIsOpen={setIsModeMenuOpen}
                model={model}
                setModel={setModel}
                modeParams={modeParams}
                setModeParams={setModeParams}
                onPresetApplied={() => {
                    clearGrid();
                    setSelectedPattern(null);
                }}
            />
            <PatternSearchModal
                isOpen={isPatternSearchOpen}
                onClose={() => setIsPatternSearchOpen(false)}
                mode={model}
                modeParams={modeParams}
                onLoadPattern={loadPattern}
            />
        </div>
    );
};

export default App;
